/**
 * Field Progress / tracker barcode payload helpers.
 *
 * Structured value: LGL|{discipline}|{rowId}|{labelKey}
 * Unstructured scans (OCR / plain labels) are treated as a label-key search.
 */

export const BARCODE_PREFIX = 'LGL';

export const DISCIPLINE_LABEL_FIELDS = {
  piping: 'isoSheetNumber',
  'piping-nde': 'isoSheetNumber',
  'piping-testing': 'isoSheetNumber',
  mechanical: 'equipmentTagOrCode',
  vessels: 'equipmentTagOrCode',
  chutes: 'equipmentTagOrCode',
  conveyors: 'equipmentTagOrCode',
  skids: 'equipmentTagOrCode',
  structural: 'equipmentTagOrCode',
  hvac: 'equipmentTagOrCode',
  ducts: 'equipmentTagOrCode',
  boilermaker: 'equipmentTagOrCode',
  electrical: 'equipmentTagOrCode',
  civil: 'equipmentTagOrCode',
};

export const labelFieldForDiscipline = (disciplineId) => (
  DISCIPLINE_LABEL_FIELDS[disciplineId] || 'equipmentTagOrCode'
);

export const labelKeyFromRow = (disciplineId, row) => {
  if (!row) return '';
  const field = labelFieldForDiscipline(disciplineId);
  const camel = row[field];
  if (camel) return String(camel).trim();
  const snake = field === 'isoSheetNumber' ? row.iso_sheet_number : row.equipment_tag_or_code;
  return String(snake || '').trim();
};

export const sanitizeLabelKey = (value) =>
  String(value || '')
    .trim()
    .replace(/\|/g, '/')
    .replace(/\s+/g, ' ');

export const buildBarcodeValue = ({ discipline, rowId, labelKey }) => {
  const key = sanitizeLabelKey(labelKey) || String(rowId);
  return `${BARCODE_PREFIX}|${discipline}|${rowId}|${key}`;
};

export const parseBarcodeValue = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  const m = text.match(/^LGL\|([^|]+)\|(\d+)\|(.*)$/i);
  if (m) {
    return {
      structured: true,
      discipline: m[1],
      rowId: Number(m[2]),
      labelKey: sanitizeLabelKey(m[3]),
      raw: text,
    };
  }
  return {
    structured: false,
    discipline: null,
    rowId: null,
    labelKey: sanitizeLabelKey(text),
    raw: text,
  };
};

const normalize = (value) => String(value || '').trim().toLowerCase();

/**
 * Rank matches for a scanned barcode / OCR string against in-memory tracker rows.
 */
export const matchScanToRows = (raw, rows, disciplineId) => {
  const parsed = parseBarcodeValue(raw);
  if (!parsed) return [];
  const list = Array.isArray(rows) ? rows : [];

  if (parsed.structured) {
    const exact = list.find((row) => (
      String(row.id) === String(parsed.rowId)
      && (!parsed.discipline || !disciplineId || parsed.discipline === disciplineId)
    ));
    if (exact) {
      return [{ row: exact, score: 100, reason: 'barcode-id', parsed }];
    }
  }

  const needle = normalize(parsed.labelKey);
  if (!needle) return [];

  const scored = [];
  for (const row of list) {
    const key = normalize(labelKeyFromRow(disciplineId, row));
    if (!key) continue;
    if (key === needle) {
      scored.push({ row, score: 90, reason: 'exact-label', parsed });
      continue;
    }
    if (key.includes(needle) || needle.includes(key)) {
      scored.push({ row, score: 70, reason: 'partial-label', parsed });
    }
  }

  return scored.sort((a, b) => b.score - a.score);
};

export const extractCandidateLabels = (ocrText) => {
  const text = String(ocrText || '');
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const candidates = [];
  for (const line of lines) {
    const structured = parseBarcodeValue(line);
    if (structured?.structured) {
      candidates.push(structured.raw);
      continue;
    }
    // Prefer tokens that look like ISO / tag identifiers (letters+digits, dashes, dots).
    const tokens = line.match(/[A-Za-z0-9][A-Za-z0-9._\-\/]{2,}/g) || [];
    candidates.push(...tokens);
    if (line.length >= 3 && line.length <= 64) candidates.push(line);
  }

  return [...new Set(
    candidates
      .map((c) => (String(c).startsWith('LGL|') ? String(c).trim() : sanitizeLabelKey(c)))
      .filter(Boolean)
  )];
};
